/**
 * SearchBar — the unified query input.
 *
 * Holds the in-progress text locally (so typing doesn't thrash the URL) and
 * commits to the URL only on submit (Enter / the Search button). Labelled for
 * a11y; the input exposes role="searchbox" via `type="search"`.
 */
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { useEffect, useState, type FormEvent, type ReactElement } from "react";
import type { SearchMode } from "./urlState";

const PLACEHOLDER: Record<SearchMode, string> = {
  keyword: 'Search records (supports "phrases", AND/OR/NOT)…',
  semantic: "Describe what you're looking for…",
  ask: "Ask a question about the records…",
};

export interface SearchBarProps {
  /** The committed query from the URL. */
  value: string;
  mode: SearchMode;
  onSubmit: (query: string) => void;
}

export function SearchBar({ value, mode, onSubmit }: SearchBarProps): ReactElement {
  const [draft, setDraft] = useState(value);

  // Keep the draft in sync when the URL query changes externally (deep links,
  // clear-all, back/forward). This mirrors URL → input, not the reverse.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    onSubmit(draft.trim());
  };

  return (
    <Box component="form" role="search" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <TextField
        fullWidth
        type="search"
        label="Search query"
        placeholder={PLACEHOLDER[mode]}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton type="submit" aria-label="Run search" edge="end">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
}
